import React from "react";
import styled from "styled-components";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PromptRibbon from "../components/Ribbon";

/* ===== Layout Shell ===== */
const PageShell = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  background: #fff;
`;
const Main = styled.main`
  flex: 1 0 auto;
  padding-top: clamp(60px, 7vw, 80px); /* space under fixed navbar */
`;

/* ===== Hero ===== */
const HeaderSection = styled.section`
  position: relative;
  overflow: clip;
  padding: clamp(2.25rem, 5vw, 4rem) 1rem clamp(2rem, 4vw, 3rem);
  background: radial-gradient(
    1000px 600px at 10% -10%,
    #4e89aa 0%,
    #2a5a7a 45%,
    #244d66 70%,
    #1e3e52 100%
  );
  color: #fff;
  isolation: isolate;

  &::before,
  &::after {
    content: "";
    position: absolute;
    inset: auto;
    border-radius: 50%;
    filter: blur(48px);
    opacity: 0.25;
    pointer-events: none;
    transform: translateZ(0);
  }
  &::before {
    width: 520px;
    height: 520px;
    left: -160px;
    top: -180px;
    background: radial-gradient(
      circle at 30% 30%,
      #7fd1ff,
      #3dccc7 60%,
      transparent 70%
    );
  }
  &::after {
    width: 580px;
    height: 580px;
    right: -220px;
    bottom: -220px;
    background: radial-gradient(
      circle at 70% 70%,
      #ffb489,
      #ff7f50 55%,
      transparent 70%
    );
  }
`;

const HeaderContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-height: 180px;

  @media (max-width: 600px) {
   max-height: 140px;
  }
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: clamp(0.75rem, 1.8vw, 0.85rem);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #c7e7f7;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.4rem 0.7rem;
  border-radius: 999px;
`;
const PageTitle = styled.h1`
  margin: 0.6rem 0 0.4rem;
  font-weight: 800;
  line-height: 1.08;
  font-size: clamp(1.6rem, 5vw, 2.4rem);
`;
const PageSubtitle = styled.p`
  margin: 0.25rem 0 0;
  opacity: 0.95;
  font-size: clamp(0.95rem, 2.2vw, 1.05rem);
  color: #e8f6ff;
`;

/* ===== Body ===== */
const SectionWrap = styled.section`
  padding: clamp(1.5rem, 4vw, 2.5rem) 1rem;
  background: #f7f8fa;
`;
const Article = styled.article`
  max-width: 900px;
  margin: 0 auto;
  background: #fff;
  border: 1px solid #eef2f6;
  border-radius: 14px;
  padding: clamp(1rem, 3.5vw, 1.5rem);
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);

  h2 {
    font-size: clamp(1.1rem, 3.5vw, 1.3rem);
    margin: 1rem 0 0.5rem;
    color: #111827;
  }
  p, li {
    color: #4b5563;
    line-height: 1.6;
    font-size: clamp(0.95rem, 2.4vw, 1rem);
  }
  ul {
    padding-left: 1.1rem;
    margin: 0.25rem 0 0.75rem;
  }
  hr {
    border: none;
    border-top: 1px solid #eef2f6;
    margin: 1rem 0;
  }
  .meta {
    color: #6b7280;
    font-size: 0.9rem;
  }
`;

const PrivacyPolicy = () => {
  return (
    <>
      <Navbar />
      <PageShell>
        <Main>
          <HeaderSection>
            <HeaderContent>
              <Eyebrow>Legal</Eyebrow>
              <PageTitle>Privacy Policy</PageTitle>
              <PageSubtitle>Last updated:  October 06, 2025</PageSubtitle>
            </HeaderContent>
          </HeaderSection>

          <SectionWrap>
            <Article>
              <p className="meta">
                AI Building Skills (“we,” “us,” or “our”) is committed to
                protecting your privacy. This Privacy Policy explains how we
                collect, use, and share information when you visit our website,
                sign up for our content, or use our services.
              </p>
              <hr />

              <h2>1. Information We Collect</h2>
              <p>We collect the following types of personal information when you interact with our site:</p>
              <ul>
                <li>
                  <strong>Name and Email Address</strong> — when you sign up for free downloads,
                  webinars, newsletters, or courses
                </li>
                <li>
                  <strong>Billing Details</strong> — if you purchase a course or service (handled
                  securely by our payment provider)
                </li>
                <li>
                  <strong>Usage Data</strong> — information about how you use the site (via cookies,
                  analytics tools, etc.)
                </li>
              </ul>

              <h2>2. How We Use Your Information</h2>
              <ul>
                <li>Send free resources or the ebook you requested</li>
                <li>Notify you about upcoming webinars or offers (if you opted in)</li>
                <li>Provide customer support</li>
                <li>Improve our content and user experience</li>
                <li>Process transactions if you purchase paid offerings</li>
              </ul>

              <h2>3. Email Communications</h2>
              <p>If you join our email list, we may send you:</p>
              <ul>
                <li>Educational content</li>
                <li>Event or webinar announcements</li>
                <li>Product or course updates</li>
                <li>Exclusive offers</li>
              </ul>
              <p>
                You can unsubscribe from these emails at any time by clicking the
                “Unsubscribe” link at the bottom of any email.
              </p>

              <h2>4. Sharing Your Information</h2>
              <p>
                We do not sell or rent your personal information to third parties.
                We may share data with trusted third-party tools (e.g., email platforms,
                payment processors) only to operate our business and deliver services.
                These third parties are bound by confidentiality agreements.
              </p>

              <h2>5. Cookies and Analytics</h2>
              <p>We may use cookies or tracking pixels to:</p>
              <ul>
                <li>Remember your preferences</li>
                <li>Track usage of the site (using tools like Google Analytics or similar)</li>
              </ul>
              <p>
                You can disable cookies through your browser settings, but it may impact site functionality.
              </p>

              <h2>6. Data Security</h2>
              <p>
                We implement security measures to protect your personal information. However,
                no online platform can guarantee 100% security, and you acknowledge that any data
                shared online is done at your own risk.
              </p>

              <h2>7. Your Rights</h2>
              <p>You may request to:</p>
              <ul>
                <li>View what data we have collected about you</li>
                <li>Correct or update your personal information</li>
                <li>Delete your data from our records</li>
              </ul>
              <p>
                To request this, email us at{" "}
                <a href="mailto:abhisheks@aibuildingskills.com">
                  abhisheks@aibuildingskills.com
                </a>.
              </p>

              <h2>8. Children’s Privacy</h2>
              <p>
                Our services are not directed to individuals under 13. We do not knowingly collect
                personal information from children.
              </p>

              <h2>9. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. The most recent version will
                always be available on this page with a revised “Last updated” date.
              </p>

              <h2>10. Contact Us</h2>
              <p>
                If you have questions or concerns about your privacy, please reach out to:
                <br />
                <strong>Abhishek Shete</strong>
                <br />
                <a href="mailto:abhisheks@aibuildingskills.com">
                  abhisheks@aibuildingskills.com
                </a>
              </p>
            </Article>
          </SectionWrap>
        </Main>

        <Footer />
        <PromptRibbon />
      </PageShell>
    </>
  );
};

export default PrivacyPolicy;
